import React, { useState } from "react";
import { Form, Icon, Checkbox, Card, Ref } from "semantic-ui-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import sortBy from "lodash/sortBy";
import { getStudentInput } from "../GraphQL";

const SortableFieldItem = ({ field, onCheckChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: field.id });

  const style = {
    margin: "0.25em 0.5em",
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Ref innerRef={setNodeRef}>
      <Card fluid style={style} {...attributes} {...listeners}>
        <Card.Content>
          <Form.Field key={field.id}>
            <Icon className="grippable" name="bars" size="small" />
            <Checkbox
              checked={field.checked}
              label={field.label}
              onChange={(e, { checked }) => {
                onCheckChange(field.id, checked);
              }}
            />
          </Form.Field>
        </Card.Content>
      </Card>
    </Ref>
  );
};

const SortableFieldList = ({ fields, onFieldChecked, onSortEnd }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = findIndex(fields, { id: active.id });
      const newIndex = findIndex(fields, { id: over.id });
      onSortEnd(oldIndex, newIndex);
    }
  }

  return (
    <Card.Group style={{ margin: "0 -0.5em" }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={fields} strategy={verticalListSortingStrategy}>
          {fields.map((field) => (
            <SortableFieldItem
              key={field.id}
              field={field}
              onCheckChange={onFieldChecked}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Card.Group>
  );
};

const StudentQuestionsForm = ({
  student,
  onCancel,
  onDelete,
  onSubmit,
  isNewStudent,
}) => {
  const [fieldItems, setFieldItems] = useState(() => {
    let fi = student.allFields.map((f) => {
      const fieldIndex = student.fieldIds.indexOf(f.id);
      return {
        checked: fieldIndex !== -1,
        originalOrder: fieldIndex !== -1 ? fieldIndex : Number.MAX_SAFE_INTEGER,
        ...f,
      };
    });
    return sortBy(fi, "originalOrder");
  });

  const handleCheckboxChange = (fieldId, checked) => {
    const newFieldItems = fieldItems.slice();
    const field = find(newFieldItems, { id: fieldId });
    field.checked = checked;
    setFieldItems(newFieldItems);
  };

  const onSortEnd = (oldIndex, newIndex) => {
    setFieldItems(arrayMove(fieldItems, oldIndex, newIndex));
  };

  return (
    <Form
      onSubmit={(evt) => {
        evt.preventDefault();
        const updatedStudent = Object.assign({}, student, {
          fieldIds: fieldItems
            .filter((i) => i.checked === true)
            .map((i) => i.id),
        });
        onSubmit(getStudentInput(updatedStudent));
      }}
    >
      <Form.Field>
        <SortableFieldList
          fields={fieldItems}
          onSortEnd={onSortEnd}
          distance={2}
          onFieldChecked={(fieldId, checked) => {
            handleCheckboxChange(fieldId, checked);
          }}
        />
      </Form.Field>
      <Form.Group>
        <Form.Button
          type="submit"
          basic
          primary
          disabled={!fieldItems.find((f) => f.checked)}
        >
          Save
        </Form.Button>
        <Form.Button type="button" basic onClick={onCancel}>
          Cancel
        </Form.Button>
      </Form.Group>
    </Form>
  );
};

export default StudentQuestionsForm;
